import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { environment } from '../../../environments/environment';
import { AgentReadyMessage } from '../../generated/models';

@Injectable({
  providedIn: 'root'
})
export class SessionHub {
  private connection: HubConnection | null = null;
  private isConnecting = false;
  private onAgentReadyCallback: ((data: AgentReadyMessage) => void) | null = null;

  /**
   * Connect to the session SignalR hub
   * @param token Token used for authentication (passed as Bearer via accessTokenFactory)
   */
  async connect(token: string): Promise<void> {
    // Always abandon any prior connection — its token might be stale, and
    // even if state === Connected we don't want to silently reuse it for a
    // different session. Fire-and-forget stop(); the WS close frame is
    // sent synchronously, ack waiting is what hangs us in Reconnecting.
    if (this.connection) {
      const old = this.connection;
      this.connection = null;
      old.stop().catch(() => {});
    }

    this.isConnecting = true;

    try {
      const hubUrl = `${environment.generatedApiUrl}/hubs/session`;

      this.connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          accessTokenFactory: () => token
        })
        .withAutomaticReconnect()
        .build();

      this.registerEventHandlers();
      await this.connection.start();
      console.log('✅ Connected to session hub');
    } catch (error) {
      console.error('❌ Failed to connect to session hub:', error);
      throw error;
    } finally {
      this.isConnecting = false;
    }
  }

  private registerEventHandlers(): void {
    if (!this.connection) return;
    if (this.onAgentReadyCallback) {
      this.connection.on('AgentReady', this.onAgentReadyCallback);
    }
  }

  /**
   * Disconnect from the session SignalR hub
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connection = null;
      console.log('🔌 Disconnected from session hub');
    }
  }

  /**
   * Get the current connection state
   */
  get connectionState(): HubConnectionState | null {
    return this.connection?.state || null;
  }

  /**
   * Subscribe to AgentReady events
   * ⚠️ DO NOT CALL — documentation only. Shows the shape of 'AgentReady' event delivered via /hubs/session.
   */
  onAgentReady(callback: (data: AgentReadyMessage) => void): void {
    this.onAgentReadyCallback = callback;
    if (this.connection?.state === 'Connected') {
      this.connection.on('AgentReady', callback);
    }
  }
}
